import { EntityList } from "@dash/entity-list";
import { BoolFilter, FilterList, SelectFilter } from "@dash/filters";
import { SearchBox } from "@dash/search";
import { QuerySkeleton } from "@dash/skeleton";
import React from "react";
import { Link } from "react-router-dom-v5-compat";

import { megaraxRoles } from "@megaron/access-control";
import { GroupSimpleDto } from "@megaron/auth-contracts";
import { useClientManager } from "@megaron/react-clients";
import { Uuid } from "@megaron/uuid";

import { getGroupPath } from "./paths";
import { parseIsArchivedOption, parseIsArchivedValue, useGroupListFilters } from "./useGroupListFilters";

type Props<TSelected> =
  | {
      isSelectable: true;
      onSelect: (groups: (GroupSimpleDto | TSelected)[]) => void;
      selected: TSelected[];
    }
  | { isSelectable?: false; onSelect?: never; selected?: never };

const roleOptions = megaraxRoles.map((role) => ({ label: role, value: role }));

export const GroupList = <TSelected extends { id: Uuid }>(props: Props<TSelected>) => {
  const { filters, setFilter } = useGroupListFilters();
  const isArchived = parseIsArchivedOption(filters.isArchived);

  const groupsQuery = useClientManager("auth")
    .groupsQuery()
    .useQuery({
      searchText: filters.searchText,
      isActive: isArchived === undefined ? undefined : !isArchived,
      role: filters.roles.length > 0 ? filters.roles : undefined,
    });

  return (
    <>
      <SearchBox onChange={setFilter("searchText")} value={filters.searchText} css={{ marginBottom: "1rem" }} />
      <FilterList>
        <BoolFilter
          label="Zarchiwizowane?"
          value={isArchived}
          onChange={(value) => setFilter("isArchived")(parseIsArchivedValue(value))}
          trueLabel="Tak"
          falseLabel="Nie"
        />
        <SelectFilter
          label="Rola"
          initiallySelectedValues={filters.roles}
          options={roleOptions}
          onSelectedChange={(options) => setFilter("roles")(options.map((o) => o.value))}
          variant="multi-select"
        />
      </FilterList>
      <QuerySkeleton query={groupsQuery}>
        {(groups) => (
          <EntityList
            items={groups}
            getKey={(g) => g.id}
            {...props}
            columns={(item) => [
              {
                header: "Stanowisko",
                isPrimary: true,
                content: (
                  <Link to={getGroupPath(item.id)} css={{ opacity: item.isActive ? 1 : 0.4 }}>
                    {item.name}
                  </Link>
                ),
              },
              { header: "Użytkownicy", content: item.accountsCount },
              { header: "Aktywne?", content: item.isActive ? "Aktywne" : "Zarchiwizowane" },
            ]}
          />
        )}
      </QuerySkeleton>
    </>
  );
};
